import { oc } from '@orpc/contract';
import { OperationEngineTargetSchema } from '@standandforce/api-contract';
import { z } from 'zod';

/**
 * Labor Core 年末調整 内容確認（タグ別インスタンス一覧）。
 * Backend: list-year-end-adjustment-instances-by-tag.api-contract.ts
 */

const yearEndAdjustmentDocumentStatusValues = ['notApplicable', 'notSubmitted', 'submitted', 'returned', 'confirmed'] as const;
const yearEndAdjustmentOverallStatusValues = ['completed', 'returned', 'awaitingReview', 'pendingAction'] as const;
const listOrderFields = z.enum(['target', 'overallStatus', 'dueDate', 'lastUpdatedAt']);
const documentFocusFields = z.enum(['dependentDeduction', 'spouseDeduction', 'insurancePremium', 'housingLoan', 'previousEmployer']);
const yearEndAdjustmentDocumentItemKeyValues = ['dependentDeduction', 'spouseDeduction', 'insurancePremium', 'housingLoan', 'previousEmployer'] as const;
export const YearEndAdjustmentDocumentItemKeySchema = z.enum(yearEndAdjustmentDocumentItemKeyValues);
export type YearEndAdjustmentDocumentItemKey = z.infer<typeof YearEndAdjustmentDocumentItemKeySchema>;
export const YearEndAdjustmentDocumentStatusSchema = z.enum(yearEndAdjustmentDocumentStatusValues);
export const YearEndAdjustmentOverallStatusSchema = z.enum(yearEndAdjustmentOverallStatusValues);
export const YearEndAdjustmentDocumentStatusItemSchema = z.object({
  itemKey: YearEndAdjustmentDocumentItemKeySchema,
  status: YearEndAdjustmentDocumentStatusSchema,
  /**
   * システム外（紙等）で別途対応する書類かどうか。BE ロールアウト差異に耐性を
   * 持たせるため nullish とし、`true` のみをフラグ扱いする。
   */
  separateHandling: z.boolean().nullish()
});
export type YearEndAdjustmentDocumentStatusItem = z.infer<typeof YearEndAdjustmentDocumentStatusItemSchema>;
export const ListYearEndAdjustmentInstancesByTagRowSchema = z.object({
  operationPackageInstanceId: z.string(),
  target: OperationEngineTargetSchema,
  overallStatus: YearEndAdjustmentOverallStatusSchema,
  /** 書類別ステータス（カタログ順） */
  items: z.array(YearEndAdjustmentDocumentStatusItemSchema),
  dueDate: z.string().nullable(),
  lastUpdatedAt: z.string().nullable()
});
export const ListYearEndAdjustmentInstancesByTagInputSchema = z.object({
  tagId: z.string(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
  orderBy: listOrderFields.optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  searchText: z.string().max(500).optional(),
  status: z.array(YearEndAdjustmentOverallStatusSchema).optional(),
  documentFocus: documentFocusFields.optional()
});
export const ListYearEndAdjustmentInstancesByTagOutputSchema = z.object({
  items: z.array(ListYearEndAdjustmentInstancesByTagRowSchema),
  length: z.number(),
  count: z.number(),
  page: z.number(),
  maxPage: z.number()
});
export const listYearEndAdjustmentInstancesByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/year-end-adjustment-instances'
}).input(ListYearEndAdjustmentInstancesByTagInputSchema).output(ListYearEndAdjustmentInstancesByTagOutputSchema);
export type YearEndAdjustmentDocumentStatus = z.infer<typeof YearEndAdjustmentDocumentStatusSchema>;
export type YearEndAdjustmentOverallStatus = z.infer<typeof YearEndAdjustmentOverallStatusSchema>;
export type ListYearEndAdjustmentInstancesByTagInput = z.infer<typeof ListYearEndAdjustmentInstancesByTagInputSchema>;
export type ListYearEndAdjustmentInstancesByTagOutput = z.infer<typeof ListYearEndAdjustmentInstancesByTagOutputSchema>;
export type ListYearEndAdjustmentInstancesByTagRowOutput = z.infer<typeof ListYearEndAdjustmentInstancesByTagRowSchema>;
export const CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema = z.object({
  tagId: z.string(),
  searchText: z.string().max(500).optional(),
  status: z.array(YearEndAdjustmentOverallStatusSchema).optional()
});
export const CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema = z.object({
  all: z.number(),
  /** 書類タブ別件数（カタログ順） */
  items: z.array(z.object({
    itemKey: YearEndAdjustmentDocumentItemKeySchema,
    count: z.number()
  }))
});
export const countYearEndAdjustmentInstancesByTagDocumentTabsContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/year-end-adjustment-instances/document-tab-counts'
}).input(CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema).output(CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema);
export type CountYearEndAdjustmentInstancesByTagDocumentTabsInput = z.infer<typeof CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema>;
export type CountYearEndAdjustmentInstancesByTagDocumentTabsOutput = z.infer<typeof CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema>;

/**
 * Labor Core: 年末調整 インスタンス書類一覧（ドロワー用）。
 * Backend: GET /v2/boe/packages/HRF_LABOR_CORE/year-end-adjustment-instances/{operationPackageInstanceId}/documents
 * 書類ごとに itemKey でキー付けされた items[] を返す。
 */

export const GetYearEndAdjustmentInstanceDocumentsInputSchema = z.object({
  operationPackageInstanceId: z.string()
});
export const YearEndAdjustmentDocumentItemSchema = z.object({
  itemKey: YearEndAdjustmentDocumentItemKeySchema,
  status: YearEndAdjustmentDocumentStatusSchema,
  /** null when status is notApplicable */
  operationStepInstanceId: z.string().nullable(),
  /** FORM タスクのインスタンス ID。null when notApplicable */
  formTaskInstanceId: z.string().nullable(),
  /** 確認/差戻し用 ticketId。null when notApplicable */
  confirmationTicketId: z.string().nullable()
});
export const GetYearEndAdjustmentInstanceDocumentsOutputSchema = z.object({
  operationPackageInstanceId: z.string(),
  overallStatus: YearEndAdjustmentOverallStatusSchema,
  items: z.array(YearEndAdjustmentDocumentItemSchema)
});
export const getYearEndAdjustmentInstanceDocumentsContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/year-end-adjustment-instances/{operationPackageInstanceId}/documents'
}).input(GetYearEndAdjustmentInstanceDocumentsInputSchema).output(GetYearEndAdjustmentInstanceDocumentsOutputSchema);
export type GetYearEndAdjustmentInstanceDocumentsInput = z.infer<typeof GetYearEndAdjustmentInstanceDocumentsInputSchema>;
export type GetYearEndAdjustmentInstanceDocumentsOutput = z.infer<typeof GetYearEndAdjustmentInstanceDocumentsOutputSchema>;
export type YearEndAdjustmentDocumentItem = z.infer<typeof YearEndAdjustmentDocumentItemSchema>;
export const laborCoreYearEndAdjustmentInstancesContract = oc.router({
  listByTag: listYearEndAdjustmentInstancesByTagContract,
  documentTabCounts: countYearEndAdjustmentInstancesByTagDocumentTabsContract,
  instanceDocuments: getYearEndAdjustmentInstanceDocumentsContract
});